/**
 * Скарб: a chest somebody buried, and nobody came back for.
 *
 * Written out of the seed and its own position, the way a село is and for the
 * same reason: «Пригода» throws away country nobody is near and grows it
 * again, so anything written once and remembered is a thing that comes back
 * wrong. A treasure is arithmetic. Forget the country round it, walk back,
 * and the same chest is in the same block of rock - which also means that
 * emptying one and walking away must not refill it, and that is what
 * `Containers.markStocked` is for.
 *
 * "Можуть бути де завгодно на глибині 5-15 блоків" - so there is no clever
 * siting here at all. One to a district, dropped anywhere inside it, at a
 * depth drawn from the same hash. If the rock it lands in happens to be a
 * cave, that district simply has no treasure: a chest standing in an open
 * passage is not buried, and half the point of the thing is digging for it.
 */

/** How much country one buried chest has to itself. */
export const TREASURE_CELL = 250;

/** How deep under the surface it lies, at the least and at the most. */
export const TREASURE_MIN_DEPTH = 5;
export const TREASURE_MAX_DEPTH = 15;

function hash(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = (h * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Where this district's treasure is buried, and how deep.
 *
 * Anywhere in the district, edge to edge - unlike a village, which has to be
 * kept well inside its own five hundred so two of them can never touch. A
 * chest is one block and cannot collide with anything.
 */
export function treasureSpot(
  seed: number, gx: number, gz: number,
): { x: number; z: number; depth: number } {
  const jx = hash(gx, gz, seed + 811);
  const jz = hash(gx, gz, seed + 829);
  const jd = hash(gx, gz, seed + 853);
  const span = TREASURE_MAX_DEPTH - TREASURE_MIN_DEPTH + 1;
  return {
    x: gx * TREASURE_CELL + Math.floor(jx * TREASURE_CELL),
    z: gz * TREASURE_CELL + Math.floor(jz * TREASURE_CELL),
    depth: TREASURE_MIN_DEPTH + Math.floor(jd * span),
  };
}

/**
 * What is in one.
 *
 * Ten iron and five gold, and it is the same in every chest for now - "далі
 * будем змінювати асортимент". Item ids rather than an import, because this
 * file is about where things are buried and `items.ts` is a long way from
 * that; the check reads the same two numbers off the registry and compares.
 */
export const TREASURE_LOOT: readonly { readonly id: number; readonly count: number }[] = [
  { id: 234 /* IronIngot */, count: 10 },
  { id: 284 /* GoldIngot */, count: 5 },
];
